/**
 * Ealyx tracked redirect (sections/ealyx-redirect.liquid, docs/codebase/theme.md "Tracked redirect"): picks the
 * destination of ?to= from the section's registry (the home when it is missing or unknown), posts the visit to the
 * backend's event log (/apps/ealyx/track, type `redirect`, whatever the cookie choice: no personal data), sends the
 * destination's GA4 event and goes there as soon as gtag has it, or after at most a second and a half without analytics
 * consent.
 * ?from= is where the link sits on our own site or content (?src=, the WhatsApp bridge's name, still counts; ?from=
 * wins): the GA4 event's placement or `contact` source. On the store's `ir-a-whatsapp` page, the old bridge's links
 * keep working: without ?to= it goes to `whatsapp-chat` with ?m=, and `contact`'s source is `site` without ?from=.
 */

import { contactCode } from './ealyx-contact-code.js';
import { trackEvent } from './ealyx-track-event.js';

const MAX_WAIT_MS = 1500;
const LINK_KEYS = ['from', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
// The backend cleans and cuts each value to 64 characters; this keeps the body under its 2 KB limit.
const MAX_VALUE_LENGTH = 200;

const link = document.querySelector('[data-ealyx-redirect]');
const registry = document.querySelector('[data-ealyx-redirect-destinations]');

if (link && registry) {
  const params = new URLSearchParams(window.location.search);
  const destinations = JSON.parse(registry.textContent);
  const bridge = link.dataset.handle === 'ir-a-whatsapp' && !params.has('to');
  const requested = bridge ? 'whatsapp-chat' : (params.get('to') ?? '');
  const to = Object.hasOwn(destinations, requested) ? requested : 'home';
  const destination = destinations[to];

  let href = link.href;
  if (destination) {
    href = destination.url;
    if (destination.message) href += encodeURIComponent(params.get('m') ?? destination.message);
  }
  link.href = href;

  const from = params.get('from') || params.get('src') || null;
  const visit = { to, lang: link.dataset.lang };
  for (const key of LINK_KEYS) {
    const value = key === 'from' ? from : params.get(key);
    if (value) visit[key] = value.slice(0, MAX_VALUE_LENGTH);
  }
  fetch('/apps/ealyx/track', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'redirect', ...visit }),
    keepalive: true,
  }).catch(() => {});

  let sent = Promise.resolve();
  if (destination?.event === 'contact') {
    const source = from ?? (bridge ? 'site' : 'redirect');
    sent = trackEvent('contact', { items: [{ item_id: contactCode(source), item_name: source }] });
  } else if (destination?.event) {
    const eventParams = { ...destination.params };
    if (from && 'placement' in eventParams) eventParams.placement = from;
    sent = trackEvent(destination.event, eventParams);
  }

  const go = () => window.location.replace(href);
  Promise.race([sent, new Promise((resolve) => window.setTimeout(resolve, MAX_WAIT_MS))]).then(go);
}
